import React, { useState, useEffect } from "react";
import { generateClient } from "aws-amplify/api";
import { Resident, SearchResidentsQuery } from "../../../API";
import {
	Button,
	CircularProgress,
	Container,
	IconButton,
	LinearProgress,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import EventService from "../../../EventService";
import buildFilter from "./Filters/FilterUtils";
import { useParams } from "react-router-dom";
import { Label } from "@mui/icons-material";

const PAGE_SIZE = 25;
const client = generateClient();

const ResidentExportButton = ({
	selectedCommunity,
	selectedLevelOfCare,
	searchKey,
	initialUserInfo,
	userInfo,
}: any) => {
	const [isLoading, setIsLoading] = useState(false);
	const pid: string = useParams();
	const exportAllRecords = async () => {
		if (selectedCommunity) {
			try {
				console.log("handle download");
				await EventService.auditLog(
					"export-dir",
					pid,
					initialUserInfo,
					userInfo
				);
			} catch (err) {
				console.log("Error Updating the User", err);
			}

			setIsLoading(true);
			try {
				const searchResidents = /* GraphQL */ `
					query SearchResidents(
						$filter: SearchableResidentFilterInput
						$sort: [SearchableResidentSortInput]
						$limit: Int
						$nextToken: String
						$from: Int
					) {
						searchResidents(
							filter: $filter
							sort: $sort
							limit: $limit
							nextToken: $nextToken
							from: $from
						) {
							items {
								id
								email
								personalEmail
								address {
									addressLine1
									addressLine2
									city
									state
									zipcode
								}
								firstName
								lastName
								preferredName
								community
								activationStatus
								deathDate
								contactInfo {
									street1
									street2
									city
									state
									zip
									phones {
										value
										type
										preferred
										hidden
									}
								}
								profile {
									includeInDirectory
									externalCalendarEnabled
									aboutMe
									birthPlace
									favoriteThings
									interests
									profession
								}
								preferredFirstName
							}
							nextToken
						}
					}
				`;
				const filter = buildFilter(
					selectedCommunity,
					selectedLevelOfCare,
					searchKey
				);

				const hasFilterConditions = !!filter.and?.length;
				const filterObject = hasFilterConditions ? { filter } : {};
				let nextToken = null;
				let residentsToExport: Array<Resident | null> = [];

				do {
					const resp = (await client.graphql({
						query: searchResidents,
						variables: {
							...filterObject,
							nextToken,
							limit: PAGE_SIZE,
						},
					})) as {
						data: SearchResidentsQuery;
					};

					const listOfResidents = resp.data.searchResidents?.items || [];
					residentsToExport = residentsToExport.concat(listOfResidents);
					nextToken = resp.data.searchResidents?.nextToken || null;
				} while (nextToken);

				// Define CSV headers
				const csvHeaders = [
					{ label: "First Name", key: "firstName" },
					{ label: "Last Name", key: "lastName" },
					{ label: "Preferred Name", key: "preferredName" },
					{ label: "Preferred First Name", key: "preferredFirstName" },
					{ label: "Community", key: "community" },
					{ label: "Activation Status", key: "activationStatus" },
					{ label: "Address Line 1", key: "address.addressLine1" },
					{ label: "Address Line 2", key: "address.addressLine2" },
					{ label: "Touchtown Email", key: "email" },
					{ label: "MyUnity Email", key: "personalEmail" },
					{ label: "Include In Directory", key: "profile?.includeInDirectory" },
					{ label: "deathDate", key: "deathDate"},
					{ label: "Type1", key: "contactInfo.phones.0.type" },
					{ label: "Value1", key: "contactInfo.phones.0.value" },
					{ label: "Preferred1", key: "contactInfo.phones.0.preferred" },
					{ label: "Hidden1", key: "contactInfo.phones.0.hidden" },
					{ label: "Type2", key: "contactInfo.phones.1.type" },
					{ label: "Value2", key: "contactInfo.phones.1.value" },
					{ label: "Preferred2", key: "contactInfo.phones.1.preferred" },
					{ label: "Hidden2", key: "contactInfo.phones.1.hidden" },
					{ label: "Type3", key: "contactInfo.phones.2.type" },
					{ label: "Value3", key: "contactInfo.phones.2.value" },
					{ label: "Preferred3", key: "contactInfo.phones.2.preferred" },
					{ label: "Hidden3", key: "contactInfo.phones.2.hidden" },
					{ label: "About Me", key: "profile.aboutMe" },
					{ label: "Favorite Things", key: "profile.favoriteThings" },
					{ label: "Interests", key: "profile.interests" },
					{ label: "Profession", key: "profile.profession" },
				];

				// Create an array to hold all CSV rows
				const csvData = [csvHeaders.map((header) => header.label)];

				// Iterate through each resident and add their data to the CSV
				residentsToExport.forEach((resident) => {
					const phones = resident?.contactInfo?.phones || [];
					const row = [
						`"${resident?.firstName || ""}"`,
						`"${resident?.lastName || ""}"`,
						`"${resident?.preferredName || ""}"`,
						`"${resident?.preferredFirstName || ""}"`,
						resident?.community || "",
						resident?.activationStatus ? "True" : "False",
						resident?.address?.addressLine1 || "",
						resident?.address?.addressLine2 || "",
						resident?.email || "",
						resident?.personalEmail || "",
						resident?.profile?.includeInDirectory ? "true" : "false",
						resident?.deathDate ? `"${resident.deathDate}"` : `""`,
						phones[0]?.type || "",
						phones[0]?.value || "",
						phones[0]?.preferred ? "True" : "False",
						phones[0]?.hidden ? "True" : "False",
						phones[1]?.type || "",
						phones[1]?.value || "",
						phones[1]?.preferred ? "True" : "False",
						phones[1]?.hidden ? "True" : "False",
						phones[2]?.type || "",
						phones[2]?.value || "",
						phones[2]?.preferred ? "True" : "False",
						phones[2]?.hidden ? "True" : "False",
						`"${resident?.profile?.aboutMe?.replace(/[\r\n"]/g, " ") || ""}"`,
						`"${
							resident?.profile?.favoriteThings?.replace(/[\r\n"]/g, " ") || ""
						}"`,
						`"${resident?.profile?.interests?.replace(/[\r\n"]/g, " ") || ""}"`,
						`"${
							resident?.profile?.profession?.replace(/[\r\n"]/g, " ") || ""
						}"`,
					];
					csvData.push(row);
				});

				const fileName = "resident_data.csv";

				// data URI for the CSV
				const csvDataUri = `data:text/csv;charset=utf-8,${encodeURIComponent(
					csvData.map((row) => row.join(",")).join("\n")
				)}`;

				//download link
				const link = document.createElement("a");
				link.setAttribute("href", csvDataUri);
				link.setAttribute("download", fileName);

				// Trigger the download
				link.click();
				setIsLoading(false);
			} catch (err) {
				console.log("Error Updating the User", err);
			}
		}
	};

	useEffect(() => {
		exportAllRecords();
	}, []);
	return (
		<div
			style={{
				marginTop: "8px",
				display: "flex",
				flexDirection: "row",
				alignItems: "center",
			}}
		>
			<Button
				style={{ fontWeight: "Falsermal", marginRight: "8px" }}
				onClick={exportAllRecords}
				disabled={!selectedCommunity || isLoading}
			>
				Export
				<IconButton
					onClick={exportAllRecords}
					color="primary"
					style={{ marginLeft: "8px" }}
					disabled={!selectedCommunity || isLoading}
				>
					<GetAppIcon />
				</IconButton>
			</Button>

			{isLoading && (
				<CircularProgress
					size={24}
					thickness={6}
					color="primary"
					style={{ marginLeft: "16px" }}
				/>
			)}
		</div>
	);
};

export default ResidentExportButton;
